import { effect } from '@preact/signals';
import { audioMuted, audioPlayingId, audioUnlocked } from '../store/widget-store';
import { dcsLog } from './logger';

/**
 * 数字人语音播放器 —— 把 BE 经 WS ai.audio 下发的整段 base64 音频解码、排队、顺序播放。
 *
 * 播放层用 Web Audio API(AudioContext.decodeAudioData + BufferSource),不用 <audio> 元素:
 * widget 以 IIFE 嵌入任意第三方站点,宿主页面 CSP 的 media-src 常不含 data:/blob:,
 * `<audio src="data:...">` 会被宿主 CSP 直接拦掉(实测某客户站 media-src 仅 'self' https: + 两个 OSS 域,
 * 连 autoplay 预热的静音 WAV data URI 都被拦)。Web Audio 走内存解码,不产生媒体资源请求,
 * 不受 media-src 约束,对任意宿主站点都稳。
 *
 * 设计要点:
 *  - 队列:整条也排队,避免上一条没播完就被下一条打断;onended/解码失败都推进队列(失败不能卡死打字机)。
 *  - autoplay 兜底:进入数字人是点击链路,unlock() 在用户手势内 resume() + 播 1 采样静音解锁;
 *    ctx 仍非 running 时:未解锁→等解锁后重试本条;已解锁/已静音仍不行→静音兜底(仍触发 onStart/onEnded,
 *    让打字机照常完成,只是无声)。
 *  - 与视频状态机解耦:仅通过 onStart/onEnded/onError 回调通知 DigitalRoot(它驱动 speaking/idle + 打字机)。
 */
export interface AudioCue {
  messageId: string;
  base64: string;
  mimeType: string;
  durationMs: number;
}

export interface AudioPlayerCallbacks {
  onStart?: (cue: AudioCue) => void;
  onEnded?: (cue: AudioCue) => void;
  onError?: (cue: AudioCue) => void;
}

type AudioContextCtor = new () => AudioContext;

/** 兼容老 Safari 的 webkitAudioContext;都不支持返回 null(调用方走静音兜底)。 */
function resolveAudioContextCtor(): AudioContextCtor | null {
  const g = globalThis as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

class AudioPlayer {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private source: AudioBufferSourceNode | null = null;
  private queue: AudioCue[] = [];
  private playing: AudioCue | null = null;
  private cb: AudioPlayerCallbacks = {};
  private offUnlock: (() => void) | null = null;

  configure(cb: AudioPlayerCallbacks): void {
    this.cb = cb;
  }

  private ensureCtx(): AudioContext {
    if (!this.ctx) {
      const Ctor = resolveAudioContextCtor();
      if (!Ctor) throw new Error('AudioContext unsupported');
      this.ctx = new Ctor();
      this.gain = this.ctx.createGain();
      this.gain.connect(this.ctx.destination);
      this.gain.gain.value = audioMuted.value ? 0 : 1;
    }
    return this.ctx;
  }

  /** 入队一条语音;空闲则立即播放。 */
  enqueue(cue: AudioCue): void {
    this.queue.push(cue);
    if (!this.playing) void this.playNext();
  }

  /** 用户手势内调用:resume + 播 1 采样静音,解锁 Web Audio autoplay。 */
  unlock(): void {
    if (audioUnlocked.value) return;
    const done = () => {
      audioUnlocked.value = true;
    };
    try {
      const ctx = this.ensureCtx();
      const p = ctx.state === 'suspended' ? ctx.resume() : Promise.resolve();
      p.then(() => {
        // 手势内播一段静音 buffer,确保 iOS 系环境的 Web Audio 彻底解锁
        try {
          const src = ctx.createBufferSource();
          src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
          src.connect(this.gain!);
          src.start(0);
        } catch {
          /* ignore */
        }
        done();
      }, done); // resume 失败也置位,后续靠静音兜底
    } catch {
      done();
    }
  }

  setMuted(m: boolean): void {
    audioMuted.value = m;
    if (this.gain) this.gain.gain.value = m ? 0 : 1;
  }

  /** 重置/关闭/转人工:清队列、停播、归位。ctx 保留(关闭会丢 autoplay 解锁状态)。 */
  stopAll(): void {
    this.queue = [];
    this.playing = null;
    audioPlayingId.value = null;
    if (this.offUnlock) {
      this.offUnlock();
      this.offUnlock = null;
    }
    if (this.source) {
      try {
        this.source.onended = null;
        this.source.stop();
      } catch {
        /* ignore */
      }
      this.source = null;
    }
  }

  private async playNext(): Promise<void> {
    const cue = this.queue.shift();
    if (!cue) {
      this.playing = null;
      audioPlayingId.value = null;
      return;
    }
    this.playing = cue;
    audioPlayingId.value = cue.messageId;

    let ctx: AudioContext;
    try {
      ctx = this.ensureCtx();
    } catch (err) {
      dcsLog.warn('audio', 'AudioContext 不可用,静音兜底', err);
      this.cb.onStart?.(cue);
      this.silentFallback(cue);
      return;
    }
    if (this.gain) this.gain.gain.value = audioMuted.value ? 0 : 1;

    // 解码 base64 → AudioBuffer(纯内存操作,不受宿主 CSP media-src 约束)
    let buf: AudioBuffer;
    try {
      buf = await ctx.decodeAudioData(base64ToArrayBuffer(cue.base64));
    } catch (err) {
      if (this.playing !== cue) return; // 解码期间被 stopAll
      dcsLog.warn('audio', 'decodeAudioData 失败,静音兜底', err);
      this.cb.onStart?.(cue);
      this.silentFallback(cue);
      return;
    }
    if (this.playing !== cue) return; // 解码期间被 stopAll

    // autoplay:ctx 还 suspended 就尝试 resume(有粘滞用户激活时可直接成功)
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* ignore,下面统一按 state 判定 */
      }
      if (this.playing !== cue) return;
    }
    if (ctx.state !== 'running') {
      if (!audioUnlocked.value && !audioMuted.value) {
        // 等用户手势解锁后重试本条(不推进队列、不起打字机)
        this.waitUnlockThenRetry(cue);
        return;
      }
      // 已解锁仍被拦 / 已静音:静音兜底 —— 仍走 onStart→durationMs→onEnded,让打字机照常完成
      dcsLog.warn('audio', 'AudioContext 未 running,静音兜底', ctx.state);
      this.cb.onStart?.(cue);
      this.silentFallback(cue);
      return;
    }

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.gain!);
    this.source = src;
    src.onended = () => {
      if (this.playing === cue) this.settle(cue, 'ended');
    };
    try {
      src.start(0);
    } catch (err) {
      this.source = null;
      dcsLog.warn('audio', 'bufferSource.start 失败', err);
      this.settle(cue, 'error');
      return;
    }
    this.cb.onStart?.(cue); // 开播 → 打字机起锚
  }

  private waitUnlockThenRetry(cue: AudioCue): void {
    this.queue.unshift(cue);
    this.playing = null;
    audioPlayingId.value = null;
    if (this.offUnlock) return; // 已在等待
    this.offUnlock = effect(() => {
      if (audioUnlocked.value) {
        const off = this.offUnlock;
        this.offUnlock = null;
        if (off) off();
        if (!this.playing) void this.playNext();
      }
    });
  }

  private silentFallback(cue: AudioCue): void {
    const ms = Math.max(300, cue.durationMs || 1500);
    setTimeout(() => this.settle(cue, 'ended'), ms);
  }

  /** 单次结算(ended/error):防重(this.playing===cue),触发回调,推进队列。 */
  private settle(cue: AudioCue, kind: 'ended' | 'error'): void {
    if (this.playing !== cue) return;
    if (kind === 'ended') this.cb.onEnded?.(cue);
    else this.cb.onError?.(cue);
    this.playing = null;
    audioPlayingId.value = null;
    void this.playNext();
  }
}

let instance: AudioPlayer | null = null;

export function getAudioPlayer(): AudioPlayer {
  if (!instance) instance = new AudioPlayer();
  return instance;
}

/** 仅测试用 — 隔离单测之间状态 */
export function resetAudioPlayer(): void {
  if (instance) instance.stopAll();
  instance = null;
}
